// web/src/tutorial/quickstart/wind-rise.ts
// Wind Rise · Quick Start（最短流程：选演奏者 → 选 MIDI → 生成动画）
//
// 依据：G:\wind_rise_rust
//   src/ui/app.rs（顶栏三个页签与顺序）、avatar_panel.rs / avatar_display.rs / avatar_manager.rs（角色设置页）、
//   midi_panel.rs（MIDI处理页）、execution_panel.rs（执行操作页）、components.rs（卡片/徽章/状态块）、
//   data.rs（各参数默认值）、asset/i18n/zh-CN.json（界面原文，标签逐字对齐）。
//
// 写作口径：这一篇只讲「最短跑通」。每个参数管什么、什么时候要动，全部放在
// /details/wind-rise.html（软件界面详细说明）。

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const windRiseQuickstart: TutorialDoc = {
  key: "wind_rise",
  slug: "wind-rise",
  kind: "quickstart",
  icon: "🎺",
  title: l("Wind Rise 管乐演奏动画", "Wind Rise: wind-instrument performance"),
  subtitle: l(
    "最短流程：选一位演奏者、挑一个 MIDI、点一次生成，剩下的交给软件。全程只有三个地方需要你选，参数保持默认就够用。",
    "The short path: pick a performer, pick a MIDI file, press generate. Only three choices are yours to make — every parameter can stay at its default.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV1Hkbn6KE5e/",
    youtube: "https://www.youtube.com/watch?v=-DiYTEWQXIA",
    caption: l(
      "Wind Rise 完整操作演示：WindRise篇：吹奏类乐器动画介绍（B 站）",
      "Wind Rise full walkthrough: Windrise: Introduction (YouTube)",
    ),
    series: [
      { title: l("WindRise篇：吹奏类乐器动画原理", "From MIDI to Performance: Wind Instrument Principles Explained"), url: "https://www.bilibili.com/video/BV1Hkbn6KEA2/", youtube: "https://www.youtube.com/watch?v=8GLhnyequfU" },
      { title: l("WindRise篇：制作长笛 shape key", "Creating Shape Keys for Wind Instruments"), url: "https://www.bilibili.com/video/BV1xKbK6eEHc/", youtube: "https://www.youtube.com/watch?v=TLh51lqOhZQ" },
      { title: l("WindRise篇：人物shape key和基准姿势", "Flute Performance: Breath States & Finger Postures"), url: "https://www.bilibili.com/video/BV1vyY868E7r/", youtube: "https://www.youtube.com/watch?v=D1jdD1UpliM" },
      { title: l("WindRise篇：在Unreal里测试哥伦比娅", "MIDI to UE5: Colombina Flute Animation Test"), url: "https://www.bilibili.com/video/BV1isY96hE6P/", youtube: "https://www.youtube.com/watch?v=8Igk1oFdO1U" },
    ],
  },
  images: [
    {
      src: "/assets/image/products/wind-rise/CN/角色设置.png",
      srcEn: "/assets/image/products/wind-rise/EN/Avatar Setting.png",
      label: l("角色设置", "Avatar Settings"),
      width: 1282,
      height: 954,
    },
    {
      src: "/assets/image/products/wind-rise/CN/MIDI处理.png",
      srcEn: "/assets/image/products/wind-rise/EN/MIDI Processing.png",
      label: l("MIDI处理", "MIDI Processing"),
      width: 1282,
      height: 954,
    },
    {
      src: "/assets/image/products/wind-rise/CN/执行操作.png",
      srcEn: "/assets/image/products/wind-rise/EN/Execution.png",
      label: l("执行操作", "Execution"),
      width: 1282,
      height: 954,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "Wind Rise 用来做**管乐演奏动画**。给它一个 MIDI 文件，再给它一位演奏者，它就把整首曲子的演奏动作算出来：手指按孔、嘴唇口型、乐器按键变形，还有身体的起伏。你不需要在 3D 软件里手动摆任何一帧。",
            "Wind Rise produces **wind-instrument performance animation**. Hand it a MIDI file and a performer, and it works out the whole performance: fingers on the holes, embouchure, instrument key deformation and the body's rise and fall. You will not hand-key a single frame.",
          ),
        },
        {
          kind: "p",
          text: l(
            "你在这个软件里要做的只有三件事，顺序就是顶栏从左到右的三个页签：`⚙ 角色设置` 里选人，`🎵 MIDI处理` 里选曲子，`▶ 执行操作` 里点生成。做完之后，把生成出来的那一份文件交给 Blender 或 Unreal 里的 MusicDoll 插件，动画就落到角色身上了。",
            "There are exactly three things to do here, in the order of the three tabs across the top. `⚙ 角色设置` / Avatar Settings picks the player. `🎵 MIDI处理` / MIDI Processing picks the music. `▶ 执行操作` / Execution presses generate. Then hand the file it produces to the MusicDoll plugin in Blender or Unreal and the animation lands on your character.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("整条路只有一步会等", "Only one step makes you wait"),
          text: l(
            "生成动画时要按帧算完整首曲子，长曲子可能要等一会儿。除此之外全是选文件、点按钮，一两分钟就能走完一遍。",
            "Generating the animation walks the entire piece frame by frame, so a long piece takes a while. Everything else is picking files and pressing buttons — the rest of the trip takes a minute or two.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tuning",
      title: l("参数要细调？去另一篇", "Need to tune something? There is another page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一篇刻意不讲参数——默认值就是给大多数曲子准备的。等你跑通一遍、发现问题了（比如快速乐句糊成一团、动作发硬），再去看[软件界面详细说明](/details/wind-rise.html)：那一页按界面分区逐个参数讲，每个参数管什么、什么时候动它。",
            "This page deliberately skips parameters — the defaults suit most pieces. Once you have run through it and hit something specific (fast passages smearing together, or motion that looks stiff), open the [UI reference](/details/wind-rise.html): it walks the interface section by section and explains what each parameter controls and when to touch it.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前", "Before you start"),
      image: { image: 0 },
      blocks: [
        {
          kind: "ul",
          items: [
            l(
              "**一份 MIDI 文件**（`.mid` / `.midi`）。单声部旋律效果最好——管乐一次只吹一个音。",
              "**One MIDI file** (`.mid` / `.midi`). A single-line melody works best, since a wind instrument sounds one note at a time.",
            ),
            l(
              "**一位演奏者**。演奏者是软件自带的，不用你准备：装好之后 `角色选择` 的下拉菜单里就有，选一个就行。",
              "**A performer.** Performers ship with the software — nothing to prepare. They are already in the `角色选择` / Avatar Selection drop-down once the app is installed; just pick one.",
            ),
            l(
              "**软件本身**。到[下载页](/free/download.html)拿 `wind_rise_dist.zip` 解压即用；免费版**不需要授权码**，装好直接进主界面，内置演奏者直接能用。",
              "**The app itself.** Get `wind_rise_dist.zip` from the [download page](/free/download.html) and unzip it. The free edition needs **no license key** — it opens straight into the main window and the bundled performers work right away.",
            ),
          ],
        },
        {
          kind: "note",
          text: l(
            "全程不需要打开 Blender 或 Unreal。这个软件只负责算出动画数据，3D 软件那边是最后加载结果时才用得上。",
            "Nothing here needs Blender or Unreal to be open. This app only computes the animation data; the 3D software comes in at the very end, to load it.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：三个页签，按顺序点", "The interface: three tabs, in order"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "顶栏左边是软件名和三个页签，右边是 `🌙 深色` / `☀ 浅色` 主题开关、`语言` 菜单和 `帮助` 菜单（里面有 `关于`）。页签可以随时来回切，设置会一直留着，所以顺序不是死规矩——但第一次建议照着走。",
            "The top bar holds the app name and the three tabs on the left. On the right sit the theme switch (`🌙 深色` / Dark and `☀ 浅色` / Light), the `语言` / Language menu and the `帮助` / Help menu. You can switch tabs freely — settings are kept — so the order is not law, but follow it the first time.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "`⚙ 角色设置` —— 选演奏者，顺便核对这位演奏者吹的是什么乐器、音域是多少。",
              "`⚙ 角色设置` — choose the performer and check which instrument they play and what range it covers.",
            ),
            l(
              "`🎵 MIDI处理` —— 选 MIDI 文件、`扫描 MIDI 信息`、勾出要生成动画的轨道。",
              "`🎵 MIDI处理` / MIDI Processing — pick the MIDI file, then scan it with the `扫描` / Scan `MIDI 信息` button and tick the tracks you want animated.",
            ),
            l(
              "`▶ 执行操作` —— 生成动画，并在 `控制台输出` 里看它跑到哪一步。",
              "`▶ 执行操作` / Execution — generate the animation and watch its progress in `控制台输出` / Console Output.",
            ),
          ],
        },
        {
          kind: "note",
          text: l(
            "Wind Rise 只有这三个页签——如果你在别的 MusicDoll 软件里见过更多，那是键盘、弦乐类的产品。设置会一直留着，随时切页签都不会丢。",
            "Wind Rise has exactly these three tabs — if other MusicDoll apps showed you more, those were the keyboard and string products. Settings persist, so switching tabs never loses anything.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选演奏者", "Step 1: choose the performer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "在 `⚙ 角色设置` 页的 `角色选择` 卡片里，点开下拉菜单，选一位演奏者。选中的名字会写在 `角色概览` 里：`名称`、`乐器`、`音域`、`配置`（这位演奏者的配置文件）和 `图片`。",
            "On the `⚙ 角色设置` / Avatar Settings tab, open the drop-down in the `角色选择` / Avatar Selection card and pick a performer. The chosen name shows up in the `角色概览` / Avatar Overview card: `名称` / Name, `乐器` / Instrument, `音域` / Range, `配置` / Config (its config file) and `图片` / Image.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**你要确认的只有一件事：`乐器` 是你要用的那件乐器。** 剩下的一律不用管，`角色参数` 里的 `渐变时长(秒)` 保持 0.50、`最小音高` / `最大音高` 保持它带出来的数字就行。",
            "**The only thing to confirm is that the `乐器` / Instrument line is the instrument you mean to use.** Everything else can be left alone: keep the `渐变时长(秒)` / Transition Duration (s) value at 0.50 and leave the `最小音高` / Min Note and `最大音高` / Max Note boxes at the numbers they came with.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("下拉菜单是空的？", "Empty drop-down?"),
          text: l(
            "说明软件目录里的 `asset/avatars` 还没有角色。演奏者是用 Blender 插件做出来再导出的，做角色的过程见《自定义管乐演奏者》进阶教程；只想先试一遍的话，装好软件的默认目录里通常已经带了几位。",
            "Then `asset/avatars` next to the app holds no performers yet. Performers are built and exported from the Blender plugin — see the Custom Wind Performer tutorial. If you just want a trial run, a fresh install usually ships with a few.",
          ),
        },
        {
          kind: "note",
          text: l(
            "`角色管理` 里的 `新增角色` / `编辑角色` / `删除角色` 是给你自己做的演奏者用的。用自带角色时完全不用碰，点错了也不会破坏已有动画数据。",
            "With a bundled performer you never need the three buttons in the `角色管理` / Avatar Management card. `新增角色` / Add Avatar, `编辑角色` / Edit Avatar and `删除角色` / Delete Avatar are for performers you build yourself, and mis-clicking them cannot damage existing animation data.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI，勾出要动画的轨道", "Step 2: pick the MIDI and tick the tracks"),
      image: { image: 1 },
      blocks: [
        {
          kind: "steps",
          items: [
            {
              title: l("把 MIDI 选进来", "Bring the MIDI in"),
              text: l(
                "点 `浏览 MIDI 文件` 挑一个 `.mid` / `.midi`；或者直接把文件从资源管理器拖到虚线框 `📥 将 MIDI 文件拖放到此处` 上。选好后 `当前文件` 会显示 `文件名` 和 `完整路径`。",
                "Pick a `.mid` / `.midi` with the `浏览 MIDI 文件` / Browse MIDI File button, or drop the file onto the dashed drop zone. Once it is in, the `当前文件` / Current File block lists the `文件名` / File Name and `完整路径` / Full Path. The drop zone itself reads `📥 将 MIDI 文件拖放到此处` / Drop MIDI file here.",
              ),
            },
            {
              title: l("扫描它的轨道", "Scan its tracks"),
              text: l(
                "点 `扫描 MIDI 信息`。右侧 `轨道选择` 里会列出这个文件里所有有音符的轨道：`轨道` 号、`通道`、`乐器` 和 `音符数`。",
                "Press the `扫描 MIDI 信息` / Scan MIDI Info button. The `轨道选择` / Track Selection card on the right then lists every track in the file that carries notes, with its `轨道` / Track number, `通道` / Channel, `乐器` / Instrument and `音符数` / Notes count.",
              ),
            },
            {
              title: l("勾出你要的那一条", "Tick the one you want"),
              text: l(
                "看 `乐器` 一列，勾上旋律所在的轨道。整首曲子的旋律通常只在一条轨道上，钢琴伴奏那几条不用勾。",
                "Read the `乐器` / Instrument column and tick the track that carries the melody. In most files the melody lives on a single track; the piano accompaniment tracks can stay unticked.",
              ),
            },
            {
              title: l("点 `✅ 添加选中的轨道`", "Press `✅ 添加选中的轨道` / Add Selected Tracks"),
              text: l(
                "**勾选本身不算数，必须点这个按钮。** 点完卡片下方会出现 `已确认轨道: 3` 这样的绿条，3 就是你确认的轨道号。",
                "**Ticking alone does nothing — you must press this button.** Afterwards a green line appears under the card reading something like `已确认轨道` / Confirmed tracks: 3, where 3 is the track number you confirmed.",
              ),
            },
          ],
        },
        {
          kind: "p",
          text: l(
            "左边的 `参数设置` 全部留默认：`通道` 是 -1（意思就是全部通道）、`FPS` 是 60、`量化` 是 `无量化`。这三个值改起来有讲究，等你确实遇到问题再去[详细说明页](/details/wind-rise.html)看。",
            "Leave everything in the `参数设置` / Parameter Settings card on the left at its default: the `通道` / Channel box stays -1 (all channels), `FPS` stays 60, and the `量化` / Quantization drop-down stays `无量化` / None. These three are worth changing only for a real problem — see the [UI reference](/details/wind-rise.html) when one shows up.",
          ),
        },
        {
          kind: "note",
          text: l(
            "`✂️ 修剪 MIDI 音符` 不是必做的。它会另存一份修掉重叠与断口的 `_trimmed.mid`，遇到音符黏连、听上去不该连的音被连起来时再用。",
            "The `✂️ 修剪 MIDI 音符` / Trim MIDI Notes button is optional. It saves a separate `_trimmed.mid` with overlaps and gaps cleaned up — reach for it when notes stick together that should not.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：点生成，等控制台跑完", "Step 3: generate and watch the console"),
      image: { image: 2 },
      blocks: [
        {
          kind: "steps",
          items: [
            {
              title: l("先核对一眼 `当前配置`", "Glance at `当前配置` / Current Setup"),
              text: l(
                "`资源` 里应该是你刚选的角色和 MIDI，`参数` 里的 `轨道` 应该是你确认的那条。下面三个小块（`初始化` / `指法` / `动画`）现在都是灰色的 `未完成`。",
                "The `资源` / Resources block should show the performer and MIDI you just picked, and the `轨道` / Track row under `参数` / Parameters should be the track you confirmed. The three chips below are all grey `未完成` / Pending for now — `初始化` / Init, `指法` / Fingering and `动画` / Animation.",
              ),
            },
            {
              title: l("点 `一键执行`", "Press `一键执行` / Run All"),
              text: l(
                "在 `执行流程` 卡片里点 `一键执行`，它会先生成指法、紧接着生成动画。也可以分两次点 `1. 生成指法` 和 `2. 生成动画`，想中途检查一下就用这种点法。",
                "In the `执行流程` / Workflow card, press `一键执行` / Run All: it generates the fingering first and the animation right after. You can also press the two step buttons separately — `1. 生成指法` / 1. Generate Fingering and `2. 生成动画` / 2. Generate Animation — which is the way to go if you want to check in between.",
              ),
            },
            {
              title: l("看 `控制台输出` 往下滚", "Watch `控制台输出` / Console Output scroll"),
              text: l(
                "右边控制台会一行行报进度：解析 MIDI、生成指法、左右手关键帧、口型、乐器、活动曲线。跑完最后一行是 `WindRise报告已保存到: …`，三个状态块也会变成绿色的 `已完成`。",
                "The console on the right reports progress line by line: parsing the MIDI, generating the fingering, the left- and right-hand keyframes, lips, instrument and the activity curve. The last line reads `WindRise报告已保存到` / WindRise report saved to: …, and the three chips turn green as `已完成` / Done.",
              ),
            },
          ],
        },
        {
          kind: "note",
          text: l(
            "生成期间按钮会变灰、卡片上出现 `后台任务执行中...`。**不要连点**，任务正在跑的时候再点也没用，它会提示你等当前任务结束。",
            "While it runs, the buttons grey out and `后台任务执行中...` / Background task running... appears on the card. **Do not click repeatedly** — extra clicks do nothing but tell you to wait for the current task to finish.",
          ),
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("只想分步跑的话，顺序不能反", "If you run it in two steps, the order matters"),
          text: l(
            "`2. 生成动画` 要读 `1. 生成指法` 的产物，没生成过指法就点它，控制台只会回一句 `请先生成指法，再执行动画生成`。",
            "The `2. 生成动画` / 2. Generate Animation step reads what the `1. 生成指法` / 1. Generate Fingering step produced. Press it first and the console simply answers `请先生成指法，再执行动画生成` / Please generate the fingering first, then generate the animation.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成出来的是什么，怎么用", "What you got and how to use it"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "所有结果都写在软件自己的 `output` 目录下，你不用一个个去挑文件。真正要交给插件的是汇总文件：`output/final_result/{角色}_{MIDI}_{轨道}_report.wind_rise`。控制台最后一行给的就是它的完整路径，`复制结果` 按钮能直接把这个路径拷进剪贴板。",
            "Everything lands under the app's own `output` folder, and you never have to pick files out of it. The one to hand to the plugin is the summary file: `output/final_result/{角色}_{MIDI}_{轨道}_report.wind_rise` — the placeholders are the performer, the MIDI and the track. The console's last line gives its full path, and the `复制结果` / Copy Result button copies that path straight to the clipboard.",
          ),
        },
        {
          kind: "p",
          text: l(
            "手部、口型、乐器、身体活动是分成若干份文件写出的，报告文件把它们串在一起——**把报告文件交给插件就等于交出了全部**，所以你只需要记住这一个名字。",
            "The hand, mouth, instrument and body-activity data are written as several separate files, and the report file ties them together — **handing the report file over hands over all of it**, so this is the only name worth remembering.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "在 Blender 或 Unreal 里启用免费的 MusicDoll 插件，打开其中的 Wind Rise 演奏者面板。",
              "Enable the free MusicDoll plugin in Blender or Unreal and open its Wind Rise performer panel.",
            ),
            l(
              "在插件里选中**与生成时同一位**演奏者。",
              "Select **the same performer** you used when generating.",
            ),
            l(
              "加载那份 `_report.wind_rise`，插件会把全部动画数据写回时间线。",
              "Load the `_report.wind_rise` file and the plugin writes all the animation data onto the timeline.",
            ),
            l(
              "按播放预览，满意就直接渲染。",
              "Play it back to preview, then render.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("别单独搬走那几个数据文件", "Do not move those data files on their own"),
          text: l(
            "报告文件里记的是那些动画文件的路径。把 `output` 目录改名或搬走，插件就找不到了——要么原样放回，要么重新生成一遍。",
            "The report file stores paths to the animation files. Rename or move the `output` folder and the plugin can no longer find them — put it back or regenerate.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前的自检清单", "Check before you generate"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "生成之前花十秒对一遍，能省掉大半的返工。左边是你该看到的，右边是看到什么就说明还没准备好。",
            "Ten seconds of checking before you generate saves most of the rework. On the left is what you should see; on the right is what tells you it is not ready.",
          ),
        },
        {
          kind: "table",
          head: [l("看一眼", "Where"), l("应该是这样", "It should look like this")],
          rows: [
            [
              l("`⚙ 角色设置` → `角色选择`", "`⚙ 角色设置` / Avatar Settings → `角色选择` / Avatar Selection"),
              l(
                "写着一个演奏者的名字，不是 `未选择`；`角色概览` 里的 `乐器` 是你要用的那件。",
                "A performer's name, not `未选择` / Not selected, and the `乐器` / Instrument line in `角色概览` / Avatar Overview is the instrument you want.",
              ),
            ],
            [
              l("`🎵 MIDI处理` → `当前文件`", "`🎵 MIDI处理` / MIDI Processing → `当前文件` / Current File"),
              l(
                "`文件名` 和 `完整路径` 都有内容，指向你真正想用的那个文件。",
                "The `文件名` / File Name and `完整路径` / Full Path rows are both filled in and point at the file you actually mean to use.",
              ),
            ],
            [
              l("`🎵 MIDI处理` → `轨道选择`", "`🎵 MIDI处理` / MIDI Processing → `轨道选择` / Track Selection"),
              l(
                "卡片下方有绿条 `已确认轨道: N`，而且 N 就是旋律所在的轨道号。",
                "The green line `已确认轨道` / Confirmed tracks: N sits under the card, and N is the melody track.",
              ),
            ],
            [
              l("`▶ 执行操作` → `参数`", "`▶ 执行操作` / Execution → `参数` / Parameters"),
              l(
                "`轨道` 不是 `未选择`，`音域` 是一对合理数字，`量化` 显示 `无量化`。",
                "The `轨道` / Track row is not `未选择` / Not selected, the `音域` / Range row holds a sane pair of numbers and the `量化` / Quantization row reads `无量化` / None.",
              ),
            ],
            [
              l("`▶ 执行操作` → `控制台输出`", "`▶ 执行操作` / Execution → `控制台输出` / Console Output"),
              l(
                "跑完之后最后几行里有 `WindRise报告已保存到: …`，路径以 `.wind_rise` 结尾。",
                "After the run, the last lines contain `WindRise报告已保存到` / WindRise report saved to: … and the path ends in `.wind_rise`.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "Common questions"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l("扫描之后 `轨道选择` 是空的", "`轨道选择` / Track Selection is empty after scanning"),
          text: l(
            "要么这个 MIDI 里确实没有音符（只有鼓点或纯控制信息），要么文件本身是空的。换一个文件试试；如果控制台报 `未扫描到可用音符轨道`，基本就是这个原因。",
            "Either the MIDI genuinely has no notes (drum hits or controller data only), or the file is empty. Try another file — a console message reading `未扫描到可用音符轨道` / No usable note tracks scanned almost always means this.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("音符数比我想的少很多", "The note count is far lower than expected"),
          text: l(
            "这是正常的。管乐一次只吹一个音，同时响的音符会被合并，只留最高的那个。所以一条和弦铺底的轨道最后只会剩下一串单音。",
            "That is expected. A wind instrument sounds one note at a time, so simultaneous notes are merged and only the highest survives. A chordal backing track ends up as a single line.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("生成的音高和原来的谱子不一样？", "The generated pitches differ from the score"),
          text: l(
            "软件会自动选一个整体移调偏移，把曲子挪进这件乐器真正吹得出来的音域，控制台里 `最佳移调偏移` 那一行写的就是它。管乐音域窄，这件事几乎每首曲子都会发生。",
            "The app picks an overall transposition to bring the piece inside the range the instrument can actually play; the `最佳移调偏移` / Best transposition offset line in the console reports it. Wind ranges are narrow, so this happens on almost every piece.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("换一位演奏者，要重做第 1 步吗？", "Switching performer — start over?"),
          text: l(
            "要。指法是按演奏者的手型与音域算的，换人就回 `⚙ 角色设置` 重选，然后回 `▶ 执行操作` 重新 `一键执行`。**不用重新选 MIDI**，`▶ 执行操作` 里点一次就行。",
            "Yes. Fingering is computed from that performer's hands and range, so go back to the `⚙ 角色设置` / Avatar Settings tab, pick the new one, then run `一键执行` / Run All again on `▶ 执行操作` / Execution. **You do not need to re-pick the MIDI** — one press on the `▶ 执行操作` tab is enough.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("点了生成，半天没动静", "Nothing seems to happen after pressing generate"),
          text: l(
            "看控制台。它在按帧算整首曲子，长曲子确实要等，期间 `控制台输出` 会持续往下滚；`状态：运行中` 也说明它还在跑。只有状态变回 `状态：空闲` 才算结束。",
            "Watch the console. It walks the whole piece frame by frame, so long pieces do take a while and the `控制台输出` / Console Output keeps scrolling as it goes; `状态：运行中` / Status: Running means it is still working. Only when it returns to `状态：空闲` / Status: Idle is it done.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("同一首曲子跑两次会覆盖吗？", "Will a second run overwrite the first?"),
          text: l(
            "会。文件名由「角色 + MIDI + 轨道」拼出来，同样的组合重跑就是覆盖上一次；想留两个版本，先把 `output` 目录复制一份出来。",
            "Yes. Names are built from performer + MIDI + track, so the same combination overwrites the previous run. To keep two versions, copy the `output` folder aside first.",
          ),
        },
      ],
    },
  ],
};
